import { useCallback, useRef, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import type { Dimensions, DimensionsHook } from './types';

const getDimensionObject = (element: HTMLElement): Dimensions => {
  const { bottom, height, left, right, top, width, x, y } = element.getBoundingClientRect();

  return {
    width,
    height,
    top,
    left,
    x,
    y,
    right,
    bottom,
  };
};

export const useDimensions = (): DimensionsHook => {
  const reference = useRef<HTMLElement | null>(null);
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [dimensions, setDimensions] = useState<Dimensions>({
    width: 0,
    height: 0,
    top: 0,
    left: 0,
    x: 0,
    y: 0,
    right: 0,
    bottom: 0,
  });

  const setRef = useCallback((node: HTMLElement | null) => {
    if (node) {
      setElement(node);
    }

    reference.current = node;
  }, []);

  useIsomorphicLayoutEffect(() => {
    if (!element) {
      return;
    }
    const measure = () =>
      window.requestAnimationFrame(() => setDimensions(getDimensionObject(element)));
    measure();

    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure);

    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure);
    };
  }, [element]);

  return { ref: setRef, dimensions };
};
